import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../store/configureStore";
import { getTutorials } from "../../../store/tutorials";
import { getReportDetails, setLastPhotoNumber } from "../../../store/reportDetails";
import { getCoverData } from "../../../store/coverData";

import { getCurrentUser } from "../../../services/authService";
import { DEFAULT_REPORT_TITLE } from "../../../utils/constants";
import { CustomUTMOverview, PDFImageProps, User, UTMInspection } from "../../../utils/models";
import { tutorialConstants } from '../../../utils/constants';
import { reArrangePhotoRecord, validateWorkOrderNumber } from "../../../utils/utilities";

import TOFDScanDataItems, { TOFDScanDataItem } from "./tofdScanDataItems";
import TOFDScanPlanItems, { TOFDScanPlanItem } from "./tofdScanPlanItems";

import Modal from "../../themed/modal";
import Button from "../../themed/button";
import Elevation from "../../themed/instruction-modals/elevation";
import LoadingBadge from "../../themed/loading-badge";
import CustomTOFDReport from ".";
import VIPhotoRecordForm from "../../forms/visual-inspection/viPhotoRecordForm";
import Conditional from "../../common/conditional";
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import CustomUTMSection from "../custom-utm/custom-utm-section";
import Drawing from "../../forms/custom-lpt/drawing";
import ExecutiveSummaryForm from "../../forms/visual-inspection/executiveSummaryForm";
import FVICoverData from "../../themed/fvi-cover-data";
import MUTIsoPageForm from "../../forms/mut/mutIsoPageForm";
import ReportTitleForm from "../../forms/custom-fvi/reportTitleForm";
import SyncTime from "../../blocks/sync-time";
import Zippy from "../../themed/zippy";
import WarningNotice from "../custom-visual-inspection/warning-notice";

import useAutoSaveReport from "../../../hooks/useAutoSave";
import useProjectDetails from "../../../hooks/useProjectDetails";

import config from "../../../utils/config";
import toastService from "../../../services/toastService";

const CreateCustomTOFDReport: React.FC = () => {
    const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { isSaving } = useAppSelector(getReportDetails);
	const { fviCoverData } = useAppSelector(getCoverData);

	const [previewReport, setPreviewReport] = useState(false);
	const [coverData, setCoverData] = useState(fviCoverData);
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
    const [drawing, setDrawing] = useState<PDFImageProps[]>([]);
    const [executiveSummary, setExecutiveSummary] = useState('');
    const [photoRecord, setPhotoRecord] = useState<PDFImageProps[]>([]);
    const [tmlLocations, setTMLLocations] = useState<PDFImageProps[]>([]);
    const [scanPlan, setScanPlan] = useState<TOFDScanPlanItem[]>([]);
    const [scanData, setScanData] = useState<TOFDScanDataItem[]>([]);
    const [scanDrawing, setScanDrawing] = useState<PDFImageProps[]>([]);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);

    const reportDetails = useMemo(() => {
        return {
            coverData,
            reportTitle,
            executiveSummary,
            drawing,
            scanData,
            scanPlan,
            scanDrawing,
            utInspectionItems,
            utInspectionOverview,
            tmlLocations, 
            photoRecord,
            projectDetails,
        }
    }, [coverData, reportTitle, drawing, executiveSummary, photoRecord, projectDetails, scanData, scanDrawing, scanPlan, tmlLocations, utInspectionItems, utInspectionOverview]);

    const validationMessage = useMemo(() => {
        if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber)) {
            return 'Enter a valid work order number!';
        }

        if (reportDetails.executiveSummary.length === 0) {
            return 'Please enter an executive summary!';
        }

        if (reportDetails.photoRecord.length === 0) {
            return 'Please provide at least one photo!';
        }

        if (reportDetails.scanDrawing.length === 0) {
            return 'Please provinde at least one scan drawing';
        }

        const isScanReadingMissing = reportDetails.scanData.some((scanItem) => scanItem.scanStart === undefined || scanItem.scanEnd === undefined);
        if (isScanReadingMissing) {
            return 'Please enter a valid scan reading!'
        }

        if (reportDetails.scanPlan.length === 0) {
            return 'Please enter a scan plan!';
        }

        if (reportDetails.scanData.length === 0) {
            return 'Please upload at least one scan data!';
        }

        if (reportDetails.drawing.length === 0) {
            return 'Please upload at least one drawing!';
        }

        return ''
    }, [reportDetails]);

    const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();
    const user = getCurrentUser() as User;

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

    const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

    useEffect(() => {
        function initializeCache() {
            const cachedReport = autoSave.oldReportData as typeof reportDetails;
            if (!cachedReport) {
                if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);

                return;
            }

            if (cachedReport.utInspectionItems) {
                setUTInspectionItems(cachedReport.utInspectionItems)
            }

            if (cachedReport.utInspectionOverview) {
                setUTInspectionOverview(cachedReport.utInspectionOverview);
            }

            if (cachedReport.tmlLocations) {
                setTMLLocations(cachedReport.tmlLocations);
            }

            if (cachedReport.scanDrawing) {
                setScanDrawing(cachedReport.scanDrawing);
            }

            setCoverData(cachedReport.coverData);
            setReportTitle(cachedReport.reportTitle);
            setExecutiveSummary(cachedReport.executiveSummary);
            setDrawing(cachedReport.drawing);
            setScanPlan(cachedReport.scanPlan);
            setScanData(cachedReport.scanData);
            setPhotoRecord(reArrangePhotoRecord(cachedReport.photoRecord));
			
            dispatch(setLastPhotoNumber(cachedReport.photoRecord.length));
        }

        initializeCache();
    }, [autoSave.oldReportData, dispatch, fviCoverData]);

    useEffect(() => {
        function mapTOFDItems() {
            const isAllMapped = scanData.every((item) => item.id);
            if (!isAllMapped) {
                const mappedScanData = scanData.map((item, index) => ({ ...item, id: index + 1 }));
                setScanData(mappedScanData);
            }
        }

        mapTOFDItems();
    }, [scanData]);

    return ( 
        <Fragment>
            <Modal isVisible={previewReport} onClose={togglePreviewReport}>
                <CustomTOFDReport {...reportDetails} />
            </Modal>

            <div className="mb-4">
                <SyncTime lastSynced={autoSave.lastSaved} />
            </div>

            {projectDetails && (
                <FVICoverData
                    coverDataId={config.coverDataTemplates.tofd[projectDetails.organisationId]}
                    isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.tofd && user.role.approveReports}
                    subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} ${projectDetails.inspectionTypeName} Reports`}
                />
            )}

            <Conditional isVisible={validationMessage.length > 0}>
                <WarningNotice title='Incomplete Report' body={validationMessage} />
            </Conditional>

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm projectTitle={projectDetails?.projectTitle} onAdd={setReportTitle} />
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm initialData={coverData} onAddCoverForm={setCoverData} />
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<ExecutiveSummaryForm initialSummary={executiveSummary} onProceed={setExecutiveSummary} />
			</Zippy>

            <Zippy headerTitle="Drawing / Iso" isValid={drawing.length > 0}>
                <Drawing initialDrawing={drawing} onAdd={setDrawing} />
			</Zippy>

            <Zippy headerTitle="Scan Plan" isValid={scanPlan.length > 0}>
				<TOFDScanPlanItems tofdScanPlanItems={scanPlan} onDataChange={setScanPlan} />
			</Zippy>
            
            <Zippy headerTitle="Scan Data" isValid={scanData.length > 0}>
				<TOFDScanDataItems tofdScanDataItems={scanData} onDataChange={setScanData} />
			</Zippy>
          
            <Zippy headerTitle="Scan Drawing" isValid={scanDrawing.length > 0}>
				<MUTIsoPageForm initialImages={scanDrawing} onProceed={setScanDrawing} />
			</Zippy>

            <CustomUTMSection
                tmlLocations={tmlLocations}
                onChangeTMLLocations={setTMLLocations}
                utmItems={utInspectionItems}
                utmOverview={utInspectionOverview}
                onItemsChange={setUTInspectionItems}
                onOverviewChange={setUTInspectionOverview}
            />

            <Zippy headerTitle="General Photos" isValid={photoRecord.length > 0}>
				<VIPhotoRecordForm initialGeneralPhotos={photoRecord} onProceed={setPhotoRecord} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
        </Fragment>
     );
};


const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
    techniqueType: '',
	velocity: 0,
};
 
export default CreateCustomTOFDReport;